import {
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Stack,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { UserSearch } from "@src/appV2/User/UserSearch";
import { zodResolver } from "@hookform/resolvers/zod";
import { LoadingButton, type UseModalState } from "@src/lib/ui-react";
import { useToast } from "@src/appV2/lib";
import { getErrorMessage } from "@src/appV2/api/errors";
import { LocationAutoCompleteField } from "@src/appV2/components/LocationAutoCompleteField/LocationAutoCompleteField";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";

import { CopyWorkplaceForm, copyWorkplaceFormSchema } from "./schema";
import { type CreatedWorkplace, useCopyWorkplace } from "./useCopyWorkplace";

export interface CopyWorkplaceDialogProps {
  modalState: UseModalState;
  /**
   * When provided, the source workplace is pre-selected and shown read-only instead of
   * the search field. Used by flows that always copy from a known workplace (e.g. CHOW).
   */
  presetSourceWorkplace?: { userId: string; name: string };
  /**
   * Locks the "Is Change-of-Ownership" checkbox to checked and the source to the preset.
   * The CHOW flow requires both to be fixed.
   */
  lockSourceAndChow?: boolean;
  /** Called with the newly created workplace after a successful copy. */
  onCreated?: (workplace: CreatedWorkplace) => void;
}

/**
 * Modal form for copying an existing workplace into a new one. Extracted from
 * `CopyFromWorkplace` so it can be reused with a pre-selected, locked source and
 * change-of-ownership flag (e.g. from the facility drawer's CHOW section).
 */
export function CopyWorkplaceDialog(props: CopyWorkplaceDialogProps) {
  const { modalState, presetSourceWorkplace, lockSourceAndChow = false, onCreated } = props;
  const { showSuccessToast, showErrorToast } = useToast();

  const { mutateAsync: copyWorkplace, isLoading: isCopyWorkplaceLoading } = useCopyWorkplace();
  const workplaceForm = useForm<CopyWorkplaceForm>({
    resolver: zodResolver(copyWorkplaceFormSchema),
    defaultValues: {
      sourceWorkplace: presetSourceWorkplace,
      addressObject: undefined,
      email: "",
      phone: "",
      meta: {
        copy: {
          notes: true,
          pricingParameters: true,
        },
        isChow: lockSourceAndChow,
      },
    },
  });
  const { control, handleSubmit, watch } = workplaceForm;

  const sourceWorkplace = watch("sourceWorkplace");
  const { data: sourceProfile } = useGetFacilityProfile(
    sourceWorkplace?.userId ?? "",
    "email phone fullAddress",
    { enabled: Boolean(sourceWorkplace?.userId) }
  );

  if (!modalState.modalIsOpen) {
    return null;
  }

  return (
    <Dialog open={modalState.modalIsOpen} onClose={modalState.closeModal} maxWidth="md" fullWidth>
      <DialogTitle>
        Copy an Existing Workplace
        <IconButton
          aria-label="close"
          onClick={modalState.closeModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <form
            id="copy-workplace-form"
            onSubmit={handleSubmit(
              async (formData) => {
                try {
                  const created = await copyWorkplace({
                    name: formData.name,
                    sourceWorkplaceId: formData.sourceWorkplace.userId,
                    address: formData.addressObject?.address,
                    coordinates: formData.addressObject?.coordinates,
                    email: formData.email ? formData.email : undefined,
                    phone: formData.phone ? formData.phone : undefined,
                    meta: formData.meta,
                  });
                  showSuccessToast(`${formData.name} is created successfully`);
                  onCreated?.(created);
                  modalState.closeModal();
                } catch (error) {
                  showErrorToast(
                    getErrorMessage(error, "Something went wrong, please contact tech team.")
                  );
                }
              },
              () => {
                showErrorToast("Please fix the highlighted errors and try again.");
              }
            )}
          >
            <Stack spacing={3}>
              <FormControl fullWidth>
                <FormLabel htmlFor="parentWorkplace">Choose Existing Workplace to Copy</FormLabel>
                {lockSourceAndChow && sourceWorkplace ? (
                  <TextField
                    disabled
                    size="small"
                    value={`${sourceWorkplace.name} (${sourceWorkplace.userId})`}
                  />
                ) : (
                  <Controller
                    name="sourceWorkplace"
                    control={control}
                    render={({ field }) => (
                      <UserSearch
                        value={field.value}
                        onBlur={field.onBlur}
                        type="FACILITY"
                        placeholder="Workplace"
                        width={350}
                        maxHeight={250}
                        onSelect={field.onChange}
                      />
                    )}
                  />
                )}
              </FormControl>
              <FormControl fullWidth>
                <FormLabel htmlFor="name">New Workplace Name</FormLabel>
                <Controller
                  name="name"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      size="small"
                      inputProps={{ "data-1p-ignore": true }}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </FormControl>
              <Controller
                name="meta.isChow"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox {...field} checked={field.value} disabled={lockSourceAndChow} />
                    }
                    label="Is Change-of-Ownership"
                  />
                )}
              />
              <FormControl fullWidth>
                <FormLabel htmlFor="addressObject">New Workplace Address (optional)</FormLabel>
                <LocationAutoCompleteField
                  name="addressObject"
                  control={control}
                  placeholder={sourceProfile?.fullAddress?.formatted ?? ""}
                  inputProps={{ "data-1p-ignore": true }}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel htmlFor="phone">New Workplace Phone (optional)</FormLabel>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      size="small"
                      inputProps={{ "data-1p-ignore": true }}
                      placeholder={sourceProfile?.phone ?? ""}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth>
                <FormLabel htmlFor="email">New Workplace Email (optional)</FormLabel>
                <Controller
                  name="email"
                  control={control}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      size="small"
                      inputProps={{ "data-1p-ignore": true }}
                      placeholder={sourceProfile?.email ?? ""}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </FormControl>
              <Stack>
                <Controller
                  name="meta.copy.notes"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Copy notes"
                    />
                  )}
                />
                <Controller
                  name="meta.copy.pricingParameters"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label="Copy pricing parameters"
                    />
                  )}
                />
              </Stack>
            </Stack>
          </form>
          <Stack>
            <LoadingButton
              type="submit"
              form="copy-workplace-form"
              variant="contained"
              isLoading={isCopyWorkplaceLoading}
              disabled={isCopyWorkplaceLoading}
              sx={{ marginLeft: "auto" }}
            >
              Copy and Create
            </LoadingButton>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
